import { MenuSearch } from '@fs/zion-icon'
import { useHistory } from '@fs/zion-router'
import { Grid, Select, TextField } from '@fs/zion-ui'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../app/PageTitleContext'
import { getSearchRoute, isValidPrefix } from './util/artifactsUtils'

/**
 * Search field used on the lookup, search, and patron artifact pages.
 * On submission, it will navigate to the route corresponding to the search.
 */
const SearchBar = ({ defaultQuery = '', defaultPrefix = 'kw.' }) => {
  const [t] = useTranslation()
  usePageTitle(t('pageTitle.artifacts.search'))
  const [query, setQuery] = useState()
  const [prefix, setPrefix] = useState(defaultPrefix)
  const [errorMessage, setErrorMessage] = useState()
  const searchLabel = t('artifacts.searchBarLabel', 'Enter keywords or artifact/patron ID')
  const history = useHistory()
  const searchBarRef = useRef()
  const searchByOptions = [
    {
      value: 'kw.',
      label: `${t('artifacts.searchbar.options.keyword', 'Keyword')} (kw.)`,
    },
    {
      value: 'a.',
      label: `${t('artifacts.artifactId', 'Artifact ID')} (a.)`,
    },
    {
      value: 'mp.',
      label: `${t('artifacts.searchbar.options.memoriesPatronId', 'Memories Patron ID')} (mp.)`,
    },
    {
      value: 'cis.user.',
      label: `${t('cisUserId', 'CIS User ID')} (cis.user.)`,
    },
    {
      value: 'cis.wkca.',
      label: `${t('artifacts.searchbar.option.cisWkcaId', 'CIS WKCA ID')} (cis.wkca.)`,
    },
    {
      value: 'un.',
      label: `${t('userName', 'Username')} (un.)`,
    },
    {
      value: 'u.',
      label: `${t('contributorId', 'Contributor ID')} (u.)`,
    },
    {
      value: 'cn.',
      label: `${t('contactName', 'Contact Name')} (cn.)`,
    },
  ]

  useEffect(() => {
    searchBarRef.current.focus()
  }, [])

  useEffect(() => {
    setQuery(defaultQuery)
  }, [defaultQuery])

  const submitSearch = (e) => {
    if (query.trim().length > 0) {
      const newRoute = getSearchRoute(prefix, query)
      if (newRoute) {
        history.push(newRoute)
      } else {
        setErrorMessage(t('artifacts.invalidSearchPrefix', 'Invalid search prefix'))
      }
    }
    if (e) {
      e.preventDefault()
    }
  }

  const updateQueryAndPrefix = (updatedQuery) => {
    if (isValidPrefix(updatedQuery)) {
      setPrefix(updatedQuery)
      setQuery('')
    } else {
      setQuery(updatedQuery)
    }
  }

  return (
    <Grid>
      <p columns={2} />
      <Select
        columns={3}
        label={t('artifacts.searchPrefixHelp.label', 'Search by')}
        value={prefix}
        onChange={(e) => setPrefix(e.target.value)}
        options={searchByOptions}
      />
      <TextField
        id="searchBar"
        columns={5}
        onChange={(e) => updateQueryAndPrefix(e.target.value)}
        label={searchLabel}
        onEnterKey={submitSearch}
        value={query}
        errorMessage={errorMessage}
        Icon={MenuSearch}
        ref={searchBarRef}
      />
      <p columns={2} />
    </Grid>
  )
}

SearchBar.propTypes = {
  defaultQuery: PropTypes.string,
}

export default SearchBar
